/**
 * conversation-settings-tab.tsx — Cài đặt → Nhắn tin & tiếp nhận → Cài đặt hội thoại.
 *
 * Ba phần (docs/PHAN-BO-HOI-THOAI.md):
 *   1. Cách nhận hội thoại THEO KÊNH — Chia tự động | Hàng chờ chung | Chỉ quản lý giao (lưu ngay từng kênh),
 *      kèm "Thu hồi nếu không trả lời" theo kênh (Tắt | N phút; lưu ngay).
 *   2. Quy tắc chia tự động (áp dụng cho kênh ở chế độ Chia tự động): ưu tiên Nhân viên AI có sẵn; không có thì
 *      giao cho người Trực tuyến chưa đạt giới hạn, theo xoay vòng / ít việc nhất; khách cũ về người phụ trách cũ.
 *   3. Giới hạn hội thoại theo nhân viên.
 * Chỉ owner/admin vào được (settings-page khai báo roles).
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, Info, Loader2, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/misc'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { cn, initials } from '@/lib/utils'
import { PRESENCE_DOT, PRESENCE_LABEL, presenceOf } from '@/lib/presence'
import {
  useAutoAssign, usePresenceMap, useSaveAutoAssign, useSaveReceptionModes, useSaveReclaim,
  type AutoAssignSettings, type AutoAssignStrategy,
} from '@/hooks/use-presence'
import { PlatformTag } from '@/components/shared/platform-mark'
import {
  RECEPTION_MODES, RECEPTION_MODE_META, accessSummary, receptionWarning, type ReceptionMode,
} from '@/lib/channel-access'
import { ROLE_LABELS, avatarSrc } from './settings-utils'
import { LIMIT_MAX, buildMemberLimits, limitsToInputs, loadText, reclaimLabel, reclaimOptions } from './conversation-settings-lib'

/** `label`/`hint` là getter đọc `i18n.t()` lúc dùng — KHÔNG spread `{...item}` ở cấp module (đóng băng giá trị). */
const STRATEGIES: { id: AutoAssignStrategy; label: string; hint: string }[] = [
  {
    id: 'round_robin',
    get label() { return i18n.t('settings:conversationSettingsTab.strategies.roundRobin.label') },
    get hint() { return i18n.t('settings:conversationSettingsTab.strategies.roundRobin.hint') },
  },
  {
    id: 'least_busy',
    get label() { return i18n.t('settings:conversationSettingsTab.strategies.leastBusy.label') },
    get hint() { return i18n.t('settings:conversationSettingsTab.strategies.leastBusy.hint') },
  },
]

const hoursLabel = (count: number) => i18n.t('settings:conversationSettingsTab.windows.hours', { count })
const daysLabel = (count: number) => i18n.t('settings:conversationSettingsTab.windows.days', { count })
const WINDOWS: { h: number; label: string }[] = [
  { h: 12, get label() { return hoursLabel(12) } },
  { h: 24, get label() { return hoursLabel(24) } },
  { h: 48, get label() { return daysLabel(2) } },
  { h: 72, get label() { return daysLabel(3) } },
  { h: 168, get label() { return daysLabel(7) } },
]

function ToggleRow({
  label, hint, checked, onChange, disabled,
}: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className={cn('flex items-start justify-between gap-4 px-4 py-3', disabled && 'opacity-60')}>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
      </span>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} aria-label={label} className="mt-0.5" />
    </label>
  )
}

export function ConversationSettingsTab() {
  const { t } = useTranslation('settings')
  const { data, isLoading, isError, error } = useAutoAssign()
  const presence = usePresenceMap()
  const save = useSaveAutoAssign()
  const saveModes = useSaveReceptionModes()
  const saveReclaim = useSaveReclaim()
  const [busyChannel, setBusyChannel] = useState<string | null>(null)
  const [draft, setDraft] = useState<AutoAssignSettings | null>(null)
  const [limitInputs, setLimitInputs] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!data) return
    setDraft(data.settings)
    setLimitInputs(limitsToInputs(data.settings.memberLimits))
  }, [data])

  const dirty = useMemo(() => {
    if (!data || !draft) return false
    const next = { ...draft, memberLimits: buildMemberLimits(limitInputs) }
    return JSON.stringify(next) !== JSON.stringify(data.settings)
  }, [data, draft, limitInputs])

  if (isLoading) return <Loading label={t('settings:conversationSettingsTab.loading')} />
  if (isError || !data || !draft) return <ErrorState message={error ? apiError(error) : undefined} />

  const set = (patch: Partial<AutoAssignSettings>) => setDraft((d) => (d ? { ...d, ...patch } : d))
  const autoChannels = data.channels.filter((c) => c.receptionMode === 'auto').length

  async function onChangeMode(channelId: string, name: string, mode: ReceptionMode) {
    setBusyChannel(channelId)
    try {
      await saveModes.mutateAsync({ [channelId]: mode })
      toast.success(t('settings:conversationSettingsTab.modeSaved', { name, mode: RECEPTION_MODE_META[mode].label }))
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusyChannel(null)
    }
  }

  async function onChangeReclaim(channelId: string, name: string, value: string) {
    const minutes = Number(value) > 0 ? Number(value) : null
    setBusyChannel(channelId)
    try {
      await saveReclaim.mutateAsync({ [channelId]: minutes })
      toast.success(t('settings:conversationSettingsTab.reclaimSaved', { name, value: reclaimLabel(minutes) }))
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusyChannel(null)
    }
  }

  async function onSave() {
    try {
      await save.mutateAsync({ ...draft!, memberLimits: buildMemberLimits(limitInputs) })
      toast.success(t('settings:conversationSettingsTab.saved'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="space-y-1">
          <p>
            <Trans t={t} i18nKey="settings:conversationSettingsTab.intro.access" components={{ b: <b /> }} />
          </p>
          <p className="text-xs text-muted-foreground">
            <Trans t={t} i18nKey="settings:conversationSettingsTab.intro.rules" components={{ b: <b /> }} />
          </p>
        </div>
      </div>

      <section className="space-y-2">
        <div>
          <h2 className="text-sm font-medium">{t('settings:conversationSettingsTab.reception.heading')}</h2>
          <p className="text-xs text-muted-foreground">{t('settings:conversationSettingsTab.reception.hint')}</p>
        </div>
        <dl className="grid gap-2 rounded-lg bg-muted/40 p-3 text-xs sm:grid-cols-3">
          {RECEPTION_MODES.map((m) => (
            <div key={m}>
              <dt className="font-medium">{RECEPTION_MODE_META[m].label}</dt>
              <dd className="mt-0.5 text-muted-foreground">{RECEPTION_MODE_META[m].hint}</dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-muted-foreground">
          <Trans t={t} i18nKey="settings:conversationSettingsTab.reception.reclaimExplain" components={{ b: <b /> }} />
        </p>
        <ul className="space-y-1 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-muted-foreground">
          <li className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />{t('settings:conversationSettingsTab.reception.warnTimerFromNow')}</li>
          <li className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />{t('settings:conversationSettingsTab.reception.warnAllHours')}</li>
        </ul>
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-normal">{t('settings:conversationSettingsTab.reception.columns.channel')}</th>
                <th className="px-3 py-2 text-left font-normal">{t('settings:conversationSettingsTab.reception.columns.people')}</th>
                <th className="px-3 py-2 text-left font-normal">{t('settings:conversationSettingsTab.reception.columns.mode')}</th>
                <th className="px-3 py-2 text-left font-normal">{t('settings:conversationSettingsTab.reception.columns.reclaim')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.channels.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-muted-foreground">{t('settings:conversationSettingsTab.reception.empty')}</td></tr>
              )}
              {data.channels.map((c) => {
                const warn = receptionWarning(c.receptionMode, c.access)
                const name = c.displayName || t('settings:conversationSettingsTab.reception.unnamed')
                return (
                  <tr key={c.id} className="align-top">
                    <td className="px-3 py-2">
                      <div className="flex min-w-[180px] items-center gap-2">
                        <PlatformTag platform={c.platform} />
                        <span className="truncate">{name}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">{accessSummary(c.access)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Select value={c.receptionMode} onValueChange={(v) => onChangeMode(c.id, name, v as ReceptionMode)} disabled={busyChannel === c.id}>
                          <SelectTrigger className="h-8 w-[170px]" aria-label={t('settings:conversationSettingsTab.reception.modeAria', { name })}><SelectValue>{RECEPTION_MODE_META[c.receptionMode].label}</SelectValue></SelectTrigger>
                          <SelectContent>
                            {RECEPTION_MODES.map((m) => <SelectItem key={m} value={m}>{RECEPTION_MODE_META[m].label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {busyChannel === c.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                      </div>
                      {warn && <p className="mt-1 flex max-w-md items-start gap-1 text-xs text-warning"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{warn}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={c.reclaimAfterMinutes ? String(c.reclaimAfterMinutes) : '0'}
                        onValueChange={(v) => onChangeReclaim(c.id, name, v)}
                        disabled={busyChannel === c.id}
                      >
                        <SelectTrigger className="h-8 w-[130px]" aria-label={t('settings:conversationSettingsTab.reception.reclaimAria', { name })}>
                          <SelectValue>{reclaimLabel(c.reclaimAfterMinutes)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {reclaimOptions(c.reclaimAfterMinutes).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <div>
          <h2 className="text-sm font-medium">{t('settings:conversationSettingsTab.rules.heading')}</h2>
          <p className="text-xs text-muted-foreground">
            {autoChannels === 0
              ? t('settings:conversationSettingsTab.rules.appliesToNone')
              : t('settings:conversationSettingsTab.rules.appliesToCount', { count: autoChannels })}
          </p>
        </div>
      <div className="divide-y rounded-lg border bg-card">
        <div className="space-y-2 px-4 py-3">
          <p className="text-sm font-medium">{t('settings:conversationSettingsTab.rules.strategy')}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {STRATEGIES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => set({ strategy: s.id })}
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-left transition-colors',
                  draft.strategy === s.id ? 'border-primary bg-primary/5' : 'hover:bg-accent',
                )}
                aria-pressed={draft.strategy === s.id}
              >
                <span className="block text-sm font-medium">{s.label}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{s.hint}</span>
              </button>
            ))}
          </div>
        </div>
        <ToggleRow
          label={t('settings:conversationSettingsTab.rules.toggles.aiFirst.label')}
          hint={t('settings:conversationSettingsTab.rules.toggles.aiFirst.hint')}
          checked={draft.aiFirst}
          onChange={(v) => set({ aiFirst: v })}
        />
        <ToggleRow
          label={t('settings:conversationSettingsTab.rules.toggles.keepBotOnAssign.label')}
          hint={t('settings:conversationSettingsTab.rules.toggles.keepBotOnAssign.hint')}
          checked={draft.keepBotOnAssign}
          onChange={(v) => set({ keepBotOnAssign: v })}
        />
        <ToggleRow
          label={t('settings:conversationSettingsTab.rules.toggles.backlogOnOnline.label')}
          hint={t('settings:conversationSettingsTab.rules.toggles.backlogOnOnline.hint')}
          checked={draft.backlogOnOnline}
          onChange={(v) => set({ backlogOnOnline: v })}
        />
        <ToggleRow
          label={t('settings:conversationSettingsTab.rules.toggles.includeManagers.label')}
          hint={t('settings:conversationSettingsTab.rules.toggles.includeManagers.hint')}
          checked={draft.includeManagers}
          onChange={(v) => set({ includeManagers: v })}
        />
        <ToggleRow
          label={t('settings:conversationSettingsTab.rules.toggles.preferPreviousAssignee.label')}
          hint={t('settings:conversationSettingsTab.rules.toggles.preferPreviousAssignee.hint')}
          checked={draft.preferPreviousAssignee}
          onChange={(v) => set({ preferPreviousAssignee: v })}
        />
        <ToggleRow
          label={t('settings:conversationSettingsTab.rules.toggles.includeGroups.label')}
          hint={t('settings:conversationSettingsTab.rules.toggles.includeGroups.hint')}
          checked={draft.includeGroups}
          onChange={(v) => set({ includeGroups: v })}
        />
        <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-6">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{t('settings:conversationSettingsTab.rules.defaultLimit.label')}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('settings:conversationSettingsTab.rules.defaultLimit.hint')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number" min={1} max={LIMIT_MAX}
              value={draft.defaultLimit}
              onChange={(e) => set({ defaultLimit: Math.max(1, Math.min(LIMIT_MAX, Number(e.target.value) || 1)) })}
              className="h-8 w-20" aria-label={t('settings:conversationSettingsTab.rules.defaultLimit.label')}
            />
            <span className="text-sm text-muted-foreground">{t('settings:conversationSettingsTab.rules.defaultLimit.within')}</span>
            <Select value={String(draft.activeWindowHours)} onValueChange={(v) => set({ activeWindowHours: Number(v) })}>
              <SelectTrigger className="h-8 w-[110px]" aria-label={t('settings:conversationSettingsTab.rules.defaultLimit.windowAria')}><SelectValue /></SelectTrigger>
              <SelectContent>
                {WINDOWS.map((w) => <SelectItem key={w.h} value={String(w.h)}>{w.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      </section>

      <section className="space-y-2">
        <div>
          <h2 className="text-sm font-medium">{t('settings:conversationSettingsTab.memberLimits.heading')}</h2>
          <p className="text-xs text-muted-foreground">
            {t('settings:conversationSettingsTab.memberLimits.hint', { limit: draft.defaultLimit })}
          </p>
        </div>
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-normal">{t('settings:conversationSettingsTab.memberLimits.columns.member')}</th>
                <th className="px-3 py-2 text-left font-normal">{t('settings:conversationSettingsTab.memberLimits.columns.status')}</th>
                <th className="px-3 py-2 text-left font-normal">{t('settings:conversationSettingsTab.memberLimits.columns.open')}</th>
                <th className="px-3 py-2 text-left font-normal">{t('settings:conversationSettingsTab.memberLimits.columns.limit')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.members.map((m) => {
                // Bảng realtime (socket) mới hơn ảnh chụp lúc tải trang.
                const st = presence.data ? presenceOf(presence.data, m.id) : m.status
                const raw = limitInputs[m.id] ?? ''
                const limit = raw.trim() === '' ? draft.defaultLimit : Math.max(0, Number(raw) || 0)
                const load = loadText(m.openCount, limit)
                return (
                  <tr key={m.id}>
                    <td className="px-3 py-2">
                      <div className="flex min-w-[180px] items-center gap-2.5">
                        <Avatar className="h-7 w-7">
                          {m.avatarUrl && <AvatarImage src={avatarSrc(m.avatarUrl)} />}
                          <AvatarFallback className="text-[11px]">{initials(m.fullName)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate">{m.fullName}</p>
                          <p className="truncate text-xs text-muted-foreground">{ROLE_LABELS[m.role as keyof typeof ROLE_LABELS] ?? m.role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={cn('h-2 w-2 rounded-full', PRESENCE_DOT[st])} />
                        {PRESENCE_LABEL[st]}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                      {load.full ? <Badge variant="warning">{t('settings:conversationSettingsTab.memberLimits.full', { load: load.text })}</Badge> : load.text}
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number" min={0} max={LIMIT_MAX} inputMode="numeric"
                        placeholder={String(draft.defaultLimit)}
                        value={raw}
                        onChange={(e) => setLimitInputs((p) => ({ ...p, [m.id]: e.target.value }))}
                        className="h-8 w-24"
                        aria-label={t('settings:conversationSettingsTab.memberLimits.limitAria', { name: m.fullName })}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-background/95 py-3 backdrop-blur">
        <Button
          variant="ghost"
          disabled={!dirty || save.isPending}
          onClick={() => { setDraft(data.settings); setLimitInputs(limitsToInputs(data.settings.memberLimits)) }}
        >
          {t('settings:conversationSettingsTab.discard')}
        </Button>
        <Button onClick={onSave} disabled={!dirty || save.isPending}>
          {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
          {t('common:actions.save')}
        </Button>
      </div>
    </div>
  )
}
